/**
 * Client-safe preference constants
 * These can be imported in client components without causing build errors
 */

import { UserPreferences } from '../types/user/preferences'

/**
 * Default preferences structure for client-side usage
 * Matches the structure from preference-manager.ts but without server dependencies
 */
export const DEFAULT_PREFERENCES: Partial<UserPreferences> = {
  ui: {
    language: 'EN',
    currency: 'CAD',
    timezone: 'America/Edmonton',
    unitOfMeasure: 'METRIC',
    fontSize: 'medium',
    reduceMotion: false,
    highContrast: false
  },
  notifications: {
    enabledTypes: ['SYSTEM', 'SECURITY', 'APPOINTMENT', 'MESSAGE', 'ALERT'],
    enabledChannels: ['websocket', 'email'],
    frequency: 'immediate',
    quietHours: {
      start: null,
      end: null,
      days: []
    }
  },
  application: {
    preferredVehicleDefinitionId: null,
    itemsPerPage: 24,
    autoSave: true
  },
  localization: {
    country: 'CA',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '24h'
  }
} as const

export const DEFAULT_PRIVACY_SETTINGS = {
  profile: {
    visibility: 'public',
    emailVisibility: 'private',
    phoneVisibility: 'private'
  },
  consent: {
    categories: {
      essential: true,
      functional: true,
      analytics: false,
      marketing: false,
      advertising: false
    },
    timestamp: new Date().toISOString(),
    method: 'EXPLICIT' as const
  },
  dataRetention: {
    period: '2years',
    autoDelete: false
  }
} as const

/**
 * Storage key for user preferences in localStorage
 */
export const PREFERENCES_STORAGE_KEY = 'user_preferences'

/**
 * Preference categories for organized updates
 */
export const PREFERENCE_CATEGORIES = {
  UI: 'ui',
  NOTIFICATIONS: 'notifications',
  APPLICATION: 'application',
  LOCALIZATION: 'localization'
} as const

/**
 * Privacy setting categories
 */
export const PRIVACY_CATEGORIES = {
  PROFILE: 'profile',
  CONSENT: 'consent',
  DATA_RETENTION: 'dataRetention'
} as const

/**
 * UI preference fields that are stored in metaData
 */
export const UI_PREFERENCE_FIELDS = [
  'fontSize',
  'reduceMotion',
  'highContrast',
  'language',
  'currency',
  'country',
  'timezone',
  'unitOfMeasure'
] as const
